/**
 * Full-viewport viewer for non-Cap'n Web Apps (iframe of registry URL).
 * Cap'n Web Apps use GadgetViewerPage instead.
 */

import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ForumIcon from '@mui/icons-material/Forum'
import ShareIcon from '@mui/icons-material/Share'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useRosShell } from '../RosShellContext'
import { listRegistryEntries } from './registryApi'
import { ShareUsersDialog } from '../components/ShareUsersDialog'
import type { InviteType } from './extensionInvitesApi'

interface AppViewerState {
  name: string
  url: string
  kind?: 'app'
}

export function AppViewerPage() {
  const shell = useRosShell()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const routedState = location.state as AppViewerState | null

  const [app, setApp] = useState<AppViewerState | null>(routedState)
  const [loading, setLoading] = useState(!routedState)
  const [error, setError] = useState<string | null>(null)
  const [invite, setInvite] = useState<{ type: InviteType } | null>(null)
  const [snackbar, setSnackbar] = useState<string | null>(null)

  useEffect(() => {
    if (routedState || !id) return
    let cancelled = false
    ;(async () => {
      try {
        const entries = await listRegistryEntries(shell.apiBaseUrl, shell.token, 'app')
        const match = entries.find(e => e.id === id)
        if (cancelled) return
        if (!match) setError('App not found.')
        else setApp({ name: match.name, url: match.url, kind: 'app' })
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load app')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- routedState is a one-time initial value
  }, [id])

  const title = app?.name ?? 'Loading…'

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <IconButton size="small" onClick={() => navigate('/extensions')} aria-label="Back to Extensions">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {app && !error && (
          <>
            <Button
              size="small"
              startIcon={<ForumIcon />}
              onClick={() => shell.startNewChatWithMessage(`Help me edit the ${app.name} app.`)}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Edit
            </Button>
            {id && (
              <>
                <Button
                  size="small"
                  startIcon={<ShareIcon />}
                  onClick={() => setInvite({ type: 'share_blueprint' })}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Share
                </Button>
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setInvite({ type: 'add_member' })}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Add Users
                </Button>
              </>
            )}
          </>
        )}
      </Stack>

      <Box sx={{ flex: 1, position: 'relative' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {app && !error && (
          <Box
            component="iframe"
            src={app.url}
            title={app.name}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          />
        )}
      </Box>

      {invite && id && app && (
        <ShareUsersDialog
          open
          onClose={() => setInvite(null)}
          apiBaseUrl={shell.apiBaseUrl}
          token={shell.token}
          selfEmail={shell.email}
          type={invite.type}
          definitionId={id}
          displayName={app.name}
          onSent={(count) => {
            setInvite(null)
            setSnackbar(
              invite.type === 'add_member'
                ? `Invited ${count} user${count === 1 ? '' : 's'} to view this app`
                : `Shared with ${count} user${count === 1 ? '' : 's'}`,
            )
          }}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2500}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
      />
    </Box>
  )
}
